import ComponentManager from '@/Management/ComponentManager';
import MLStringPopupBoxViewModel from '@/ViewModels/Controls/MLStringPopupBoxViewModel';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Loader from '@/Utility/Loader';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import Utilities from '@/Utility/Utilities';
import * as DateHelper from '@/Utility/DateHelper'
import { IChangelogMLStringPopupBoxViewModelConstructorParameter } from "#/DefaultViewExtensionInterfaces";

export default class ChangelogMLStringPopupBoxViewModel extends MLStringPopupBoxViewModel {

    private readonly lockGroupId: string;
    private readonly gOPdoId: string;
    private readonly detailViewContext: DetailViewContext;

    private static readonly propertyName = 'Comment';
    private static readonly valueMlsName = `${ChangelogMLStringPopupBoxViewModel.propertyName}.ValueMLS`;
    private static readonly separatorStringName = `${ChangelogMLStringPopupBoxViewModel.propertyName}.SeparatorString`;

    private changeLogPdoID: string;

    public constructor(params: IChangelogMLStringPopupBoxViewModelConstructorParameter) {
        super(params);

        this.detailViewContext = params.detailViewContext;
        this.lockGroupId = this.detailViewContext.detailViewContextDTO.lockGroupID;
        this.gOPdoId = this.detailViewContext.genericObject.pdoId;
        this.isEachLanguageEqual(true);

        this.registerOnUIListener();
    }

    /**
     * Registriert das Control in einen UIListener
     */
    private registerOnUIListener() {
        const uiListener = this.detailViewContext.getUIListener();
        uiListener.registerOnBeforeApplyAction((_) => this.checkAndSaveChangelog());
    }

    /**
     * Führ die Prüfung inkl. Speicherung des Changelog durch
     */
    private checkAndSaveChangelog(): JQueryPromise<boolean> {
        let needsSave = this.needsSaveChangelog();
        let needsDelete = this.needsDeleteChangeLog();

        if(!needsSave && !needsDelete)
        {
            return Utilities.resolveImmediately(true);
        }

        if(needsSave)
            return this.createAndSaveChangelog();

        // Anderenfalls ist needsDelete true
        return this.deleteChangelog();
    }

    /**
     * Gibt an, ob ein bestehener Changelog gelöscht werden muss
     */
    private needsDeleteChangeLog() {
        if (!Utilities.isNullOrUndefined(this.changeLogPdoID) && this.areValuesEmpty())
            return true;

        return false;
    }

    /**
     * Gibt an, ob der Changelog gespeichert werden soll / muss
     */
    private needsSaveChangelog() {
        if (this.isRequired() || !this.areValuesEmpty())
            return true;

        return false;
    }

    /**
     * Gibt an, ob in dem Control überhaupt etwas eingetragen wurde
     */
    public areValuesEmpty() {
        const plainValues = this.getPlainValues();
        return (_.keys(plainValues).length === 0 || _.every(_.values(plainValues), (value) => Utilities.isNullOrUndefinedOrBlank(value)))
            && Utilities.isNullOrUndefinedOrBlank(this.value());
    }

    /**
     * Legt einen Changelog an bzw. lädt einen bestehenden und aktualisiert die Kommentare
     */
    private createAndSaveChangelog(): JQueryPromise<boolean> {
        Loader.getInstance().show();
        return this.createChangelogOrLock()
            .then((result) => {
                if (!result)
                    return null;

                return this.setCommentText();
            })
            .then((result) => {
                return this.validateStatus(result);
            })
            .always(() => {
                Loader.getInstance().hide();
            }).promise();
    }

    /**
     * Löscht einen Changelog
     */
    private deleteChangelog(): JQueryPromise<boolean> {
        return RADEnvironmentProxy.getInstance().deleteObject({
            pdTypeName: 'RAD.RADFoundation.ProblemDomain.Journal.Changelog',
            pdoID: this.changeLogPdoID
        }).then((result) => {
            if (!this.validateStatus(result))
                return false;

            this.changeLogPdoID = null;
            return true;
        }).promise();
    }

    /**
     * Erzeugt einen leeren Changelog oder lockt einen bestehenden
     */
    private createChangelogOrLock(): JQueryPromise<boolean> {
        if (!Utilities.isNullOrUndefined(this.changeLogPdoID)) {
            return RADEnvironmentProxy.getInstance().aquireLockObject(
                {
                    lockGroupID: this.lockGroupId,
                    pdoID: this.changeLogPdoID
                }).promise();
        }

        return RADEnvironmentProxy.getInstance().createAndLockInlineEditingObject({
            contextRelationName: 'Changelogs',
            pdoID: this.gOPdoId,
            lockGroupID: this.lockGroupId
        }).then((result: CoreFrameDTOs.IGenericDTO) => {
            if (!this.validateStatus(result))
                return false;

            // PDO-ID des neu angelegten Kommentars speichern
            this.changeLogPdoID = result.pdoId;

            // SeparatorString vom Server nehmen
            this.separatorString(result.values[ChangelogMLStringPopupBoxViewModel.separatorStringName]);

            return true;
        }).promise();
    }

    /**
     * Setzt den Text des Changelogs 
     */
    private setCommentText(): JQueryPromise<CoreFrameDTOs.IResultDTO> {
        return RADEnvironmentProxy.getInstance().setProperties({
            pdoID: this.changeLogPdoID,
            values: JSON.stringify({
                [ChangelogMLStringPopupBoxViewModel.valueMlsName]: this.value(),
                ['PDO_Changed']: DateHelper.toDotNetDateTimeString(new Date())
            })
        }).promise();
    }

}

ComponentManager.setViewModel('ChangelogMLStringPopupBox', ChangelogMLStringPopupBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');